import React, { createContext, useContext, useState, useCallback } from 'react';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import AddTagsModal from 'talent_matching/components/modals/add_tags_modal';
import MakeProspectModal from 'talent_matching/talent_rediscovery/components/modals/make_prospect_modal';
import EmailCandidateModal from 'candidate_profile_redesign/modals/email_candidate_modal';
import AddToJobModal from 'candidate_profile_redesign/modals/add_to_job_modal';
import CandidateProfileWrapper from 'talent_matching/components/side_panel/candidate_profile_wrapper';
import {
  type RediscoveryApplication,
  type RediscoveryAction,
} from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useQueryClient } from '@tanstack/react-query';
import { withLocalePrefix } from 'shared/utils/translation';
import { createRediscoveryAction } from 'talent_matching/api';

const t = withLocalePrefix('talent_matching');

type RediscoveryRowActionsModalContextValue = {
  openTagsFor: (application: RediscoveryApplication) => void;
  openProspectFor: (application: RediscoveryApplication) => void;
  openEmailFor: (application: RediscoveryApplication) => void;
  openAddToJobFor: (application: RediscoveryApplication) => void;
  hasOpenModal: boolean;
};

const RediscoveryRowActionsModalContext = createContext<
  RediscoveryRowActionsModalContextValue | undefined
>(undefined);

export const useRediscoveryRowActionsModal = () => {
  const context = useContext(RediscoveryRowActionsModalContext);
  if (!context) {
    throw new Error(
      'useRediscoveryRowActionsModal must be used within RediscoveryRowActionsModalProvider'
    );
  }
  return context;
};

type RediscoveryRowActionsModalProviderProps = {
  children: React.ReactNode;
};

export const RediscoveryRowActionsModalProvider = ({
  children,
}: RediscoveryRowActionsModalProviderProps) => {
  const {
    canCreateTags,
    canCreateSources,
    currentUserName,
    userId,
    paths: { getTagsPath },
    hiringPlanId,
    hiringPlanName,
  } = useUserContext();
  const {
    paths: {
      jobsPath,
      departmentsPath,
      officesPath,
      prospectPoolsPath,
      prospectOwnerOptionsPath,
      sourceOptionsPath,
      referrerOptionsPath,
      sourceTypeOptionsPath,
    },
    defaultPoolName,
  } = useRediscoveryTabContext();
  const queryClient = useQueryClient();

  // Tags modal state
  const [tagApplication, setTagApplication] =
    useState<RediscoveryApplication | null>(null);
  const [showTagsModal, openTagsModal, closeTagsModal] = useModal();

  // Prospect modal state
  const [prospectApplication, setProspectApplication] =
    useState<RediscoveryApplication | null>(null);
  const [showProspectModal, openProspectModal, closeProspectModal] = useModal();

  // Email modal state
  const [emailApplication, setEmailApplication] =
    useState<RediscoveryApplication | null>(null);
  const [showEmailModal, openEmailModal, closeEmailModal] = useModal();

  // Add to job modal state
  const [addToJobApplication, setAddToJobApplication] =
    useState<RediscoveryApplication | null>(null);
  const [showAddToJobModal, openAddToJobModal, closeAddToJobModal] = useModal();

  const openTagsFor = useCallback(
    (application: RediscoveryApplication) => {
      setTagApplication(application);
      openTagsModal();
    },
    [openTagsModal]
  );

  const openProspectFor = useCallback(
    (application: RediscoveryApplication) => {
      setProspectApplication(application);
      openProspectModal();
    },
    [openProspectModal]
  );

  const openEmailFor = useCallback(
    (application: RediscoveryApplication) => {
      setEmailApplication(application);
      openEmailModal();
    },
    [openEmailModal]
  );

  const openAddToJobFor = useCallback(
    (application: RediscoveryApplication) => {
      setAddToJobApplication(application);
      openAddToJobModal();
    },
    [openAddToJobModal]
  );

  const handleCloseTagsModal = useCallback(() => {
    closeTagsModal();
    setTagApplication(null);
  }, [closeTagsModal]);

  const handleCloseProspectModal = useCallback(() => {
    closeProspectModal();
    setProspectApplication(null);
  }, [closeProspectModal]);

  const handleCloseEmailModal = useCallback(() => {
    closeEmailModal();
    setEmailApplication(null);
  }, [closeEmailModal]);

  const handleCloseAddToJobModal = useCallback(() => {
    closeAddToJobModal();
    setAddToJobApplication(null);
  }, [closeAddToJobModal]);

  const handleTagsSuccess = useCallback(
    (tags: Item[]) => {
      if (!tagApplication) {
        return;
      }

      queryClient.setQueryData(['rediscovery-application', tagApplication.id], {
        ...tagApplication,
        tags,
      });

      createRediscoveryAction({
        action_type: 'tags_amended',
        application_id: tagApplication.id,
        hiring_plan_id: hiringPlanId,
        metadata: {
          tags: tags.map((tag) => tag.value),
        },
      });
    },
    [tagApplication, hiringPlanId, queryClient]
  );

  const handleProspectSuccess = useCallback(
    async (metadata: Record<string, unknown>) => {
      if (!prospectApplication) {
        return;
      }

      const result = await createRediscoveryAction({
        action_type: 'added_as_prospect',
        application_id: prospectApplication.id,
        hiring_plan_id: hiringPlanId,
        metadata,
      });

      if (result) {
        const newAction: RediscoveryAction = {
          id: result.id,
          action: 'added_as_prospect',
          metadata,
          organization_id: 0,
          hiring_plan_id: hiringPlanId,
          application_id: prospectApplication.id,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        queryClient.setQueryData(
          ['rediscovery-application', prospectApplication.id],
          {
            ...prospectApplication,
            rediscoveryActions: [
              newAction,
              ...prospectApplication.rediscoveryActions,
            ],
          }
        );
      }
    },
    [prospectApplication, hiringPlanId, userId, queryClient]
  );

  const handleEmailSuccess = useCallback(async () => {
    if (!emailApplication) {
      return;
    }

    const result = await createRediscoveryAction({
      action_type: 'sent_email',
      application_id: emailApplication.id,
      hiring_plan_id: hiringPlanId,
    });

    if (result) {
      const newAction: RediscoveryAction = {
        id: result.id,
        action: 'sent_email',
        metadata: null,
        organization_id: 0,
        hiring_plan_id: hiringPlanId,
        application_id: emailApplication.id,
        user_id: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData(
        ['rediscovery-application', emailApplication.id],
        {
          ...emailApplication,
          rediscoveryActions: [
            newAction,
            ...emailApplication.rediscoveryActions,
          ],
        }
      );
    }
  }, [emailApplication, hiringPlanId, userId, queryClient]);

  const defaultJob = null;

  const defaultOwner: Item = {
    label: currentUserName,
    value: userId.toString(),
  };

  const hasOpenModal =
    showTagsModal || showProspectModal || showEmailModal || showAddToJobModal;

  return (
    <RediscoveryRowActionsModalContext.Provider
      value={{
        openTagsFor,
        openProspectFor,
        openEmailFor,
        openAddToJobFor,
        hasOpenModal,
      }}
    >
      {children}

      {tagApplication && (
        <AddTagsModal
          isOpen={showTagsModal}
          closeModal={handleCloseTagsModal}
          getTagsPath={getTagsPath}
          addTagsPath={tagApplication.addTagsPath}
          fullName={tagApplication.fullName}
          currentTags={tagApplication.tags as Item[]}
          canCreateTags={canCreateTags}
          onSuccess={handleTagsSuccess}
        />
      )}

      {prospectApplication && (
        <MakeProspectModal
          isOpen={showProspectModal}
          closeModal={handleCloseProspectModal}
          makeProspectPath={prospectApplication.makeProspectPath}
          fullName={prospectApplication.fullName}
          jobsPath={jobsPath as string}
          departmentsPath={departmentsPath as string}
          officesPath={officesPath as string}
          prospectPoolsPath={prospectPoolsPath as string}
          prospectOwnerOptionsPath={prospectOwnerOptionsPath as string}
          sourceOptionsPath={sourceOptionsPath as string}
          referrerOptionsPath={referrerOptionsPath as string}
          defaultJob={defaultJob}
          applicationId={prospectApplication.id.toString()}
          onSuccess={handleProspectSuccess}
          sourceTypeOptionsPath={sourceTypeOptionsPath}
          canCreateSources={canCreateSources}
          defaultPoolName={defaultPoolName}
          defaultOwner={defaultOwner}
          sourceAndResponsibilityPath={
            prospectApplication.sourceAndResponsibilityPath
          }
        />
      )}

      {emailApplication && (
        <CandidateProfileWrapper
          candidateProfileContextPath={
            emailApplication.candidateProfileContextPath
          }
        >
          <EmailCandidateModal
            isOpen={showEmailModal}
            handleClose={handleCloseEmailModal}
            modalPath={emailApplication.emailCandidatePath}
            title={t('send_email_to', {
              name: emailApplication.fullName,
            })}
            onSubmit={handleEmailSuccess}
            clearSubject
          />
        </CandidateProfileWrapper>
      )}

      {addToJobApplication && (
        <CandidateProfileWrapper
          candidateProfileContextPath={
            addToJobApplication.candidateProfileContextPath
          }
        >
          <AddToJobModal
            isOpen={showAddToJobModal}
            onDismiss={handleCloseAddToJobModal}
            modalPath={addToJobApplication.addPersonToJobModalPath}
            submitPath={addToJobApplication.addPersonToJobPath}
            preselectedHiringPlan={{
              label: hiringPlanName,
              value: hiringPlanId.toString(),
            }}
          />
        </CandidateProfileWrapper>
      )}
    </RediscoveryRowActionsModalContext.Provider>
  );
};
